import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, IconButton, Alert, Snackbar, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, CircularProgress
} from '@mui/material';
import { Visibility as ViewIcon, Delete as DeleteIcon, Edit as EditIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { getEvents, updateEvent, deleteEvent } from '../../services/api';
import ConfirmDeleteDialog from '../../components/Common/ConfirmDeleteDialog';
import Loader from '../../components/Common/Loader';
import { useAuth } from '../../context/AuthContext';
import {
  toYMD, formatDate, todayYMD, hasCoords, normalizeTime, EventFormFields
} from './eventShared';

const colWidths = { phone: 120, date: 130, time: 100 };

const Events = () => {
  const { groupId } = useParams();
  const { user } = useAuth();
  const canDelete = user?.userType !== 'sub_user';

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [viewing, setViewing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [tab, setTab] = useState('current');

  // Editing only — creating a new event now lives on its own "New Event" page.
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(null);
  const [pin, setPin] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEvents(groupId);
      if (res.success) setEvents(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  // Events can arrive from the live consumer app or another admin, not just this
  // browser tab — poll quietly in the background so new/changed/deleted rows show
  // up without the user having to refresh. No spinner, no error toast on failure
  // (a single missed poll isn't worth interrupting the user over).
  useEffect(() => {
    const interval = setInterval(() => {
      // Skip while the session is gone/tearing down — avoids a burst of 401s.
      if (!localStorage.getItem('token')) return;
      getEvents(groupId)
        .then(res => { if (res.success) setEvents(res.data); })
        .catch(err => console.error('Background event refresh failed:', err.message));
    }, 15000);
    return () => clearInterval(interval);
  }, [groupId]);

  const requestRemove = (ev) => setConfirmDelete(ev);

  const handleConfirmDelete = async () => {
    const ev = confirmDelete;
    setConfirmDelete(null);
    try {
      await deleteEvent(groupId, ev.id);
      // The event is now gone from the app and from the Current list — it lives
      // on only as an archived row, which shows under Expired / Deleted. Move it
      // there locally straight away, then reconcile with the server.
      setEvents(prev => prev.map(e => e.id === ev.id ? { ...e, deleted_at: new Date().toISOString() } : e));
      setViewing(null);
      setSuccess('Event deleted — removed from the app');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove event');
    }
  };

  const openEdit = (ev) => {
    setEditingEvent(ev);
    setForm({
      event_date: toYMD(ev.event_date) || todayYMD(),
      event_time: normalizeTime(ev.event_time) || '6:00 AM',
      event_desc: ev.event_desc || ''
    });
    setPin(hasCoords(ev) ? [parseFloat(ev.lat), parseFloat(ev.lang)] : null);
  };

  const handleSaveEdit = async () => {
    if (!form.event_desc.trim()) {
      setError('Enter an event description');
      return;
    }
    if (!pin) {
      setError('Drop a pin on the map to set the event location');
      return;
    }
    setSaving(true);
    try {
      await updateEvent(groupId, editingEvent.id, {
        event_date: form.event_date,
        event_time: form.event_time,
        event_desc: form.event_desc.trim(),
        lat: pin[0],
        lang: pin[1]
      });
      setEditingEvent(null);
      setSuccess('Event updated');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const today = todayYMD();
  const isDeleted = (ev) => !!ev.deleted_at;
  const currentEvents = events.filter(ev => !isDeleted(ev) && ev.event_date && toYMD(ev.event_date) >= today);
  const expiredEvents = events.filter(ev => isDeleted(ev) || !ev.event_date || toYMD(ev.event_date) < today);
  const visibleEvents = tab === 'current' ? currentEvents : expiredEvents;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Typography variant="h4" fontWeight={600}>Event Library</Typography>
        {!loading && (
          <Typography variant="h6" color="text.secondary" fontWeight={400}>
            ({events.length})
          </Typography>
        )}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, minHeight: 0 }}>
          <Tab value="current" label={`Current (${currentEvents.length})`} sx={{ minHeight: 0 }} />
          <Tab value="expired" label={`Expired / Deleted (${expiredEvents.length})`} sx={{ minHeight: 0 }} />
        </Tabs>

        {loading ? (
          <Loader />
        ) : visibleEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No {tab === 'current' ? 'current' : 'expired/deleted'} events found
          </Typography>
        ) : (
          <>
            {/* Column headers */}
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ width: colWidths.phone }}>Ph No</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.date }}>Event Date</Typography>
              <Typography variant="subtitle2" sx={{ width: colWidths.time }}>Event Time</Typography>
              <Typography variant="subtitle2">Action</Typography>
            </Box>

            {visibleEvents.map((ev) => (
              <Box key={ev.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="body2" sx={{ width: colWidths.phone }}>{ev.admin_phone || '-'}</Typography>
                <Typography variant="body2" sx={{ width: colWidths.date }}>{formatDate(ev.event_date)}</Typography>
                <Typography variant="body2" sx={{ width: colWidths.time }}>{ev.event_time || '-'}</Typography>
                <IconButton size="small" color="success" onClick={() => setViewing(ev)}>
                  <ViewIcon fontSize="small" />
                </IconButton>
                {tab === 'current' && !isDeleted(ev) && (
                  <IconButton size="small" color="primary" onClick={() => openEdit(ev)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {tab === 'current' && !isDeleted(ev) && canDelete && (
                  <IconButton size="small" color="error" onClick={() => requestRemove(ev)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </>
        )}
      </Paper>

      {/* View event details */}
      <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Event Details</DialogTitle>
        <DialogContent dividers>
          {viewing && (
            <>
              <Typography variant="overline" color="primary" sx={{ letterSpacing: 1 }}>Details</Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 1, flexWrap: 'wrap' }}>
                <TextField
                  label="Date"
                  size="small"
                  value={formatDate(viewing.event_date)}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: '1 1 180px' }}
                />
                <TextField
                  label="Time"
                  size="small"
                  value={viewing.event_time || '-'}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: '1 1 180px' }}
                />
              </Box>
              <TextField
                label="Description / note"
                fullWidth
                size="small"
                multiline
                minRows={3}
                value={viewing.event_desc || '-'}
                InputProps={{ readOnly: true }}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="overline" color="primary" sx={{ letterSpacing: 1 }}>Location</Typography>
                {hasCoords(viewing) ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {`${parseFloat(viewing.lat).toFixed(5)}, ${parseFloat(viewing.lang).toFixed(5)}`}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(`${parseFloat(viewing.lat).toFixed(5)}, ${parseFloat(viewing.lang).toFixed(5)}`);
                        setSuccess('Coordinates copied to clipboard');
                      }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No location recorded</Typography>
                )}
              </Box>
              {hasCoords(viewing) && (
                <Box
                  sx={{
                    height: 440,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                  }}
                >
                  <MapContainer
                    center={[parseFloat(viewing.lat), parseFloat(viewing.lang)]}
                    zoom={16}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; OpenStreetMap contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[parseFloat(viewing.lat), parseFloat(viewing.lang)]}>
                      <Popup>
                        {formatDate(viewing.event_date)} at {viewing.event_time || '-'}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {viewing && !isDeleted(viewing) && toYMD(viewing.event_date) >= today && (
            <Button
              onClick={() => { const ev = viewing; setViewing(null); openEdit(ev); }}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          )}
          <Button variant="contained" onClick={() => setViewing(null)} sx={{ minWidth: 120 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit event */}
      <Dialog open={!!editingEvent} onClose={() => !saving && setEditingEvent(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Edit Event</DialogTitle>
        <DialogContent dividers>
          {form && (
            <EventFormFields form={form} setForm={setForm} pin={pin} setPin={setPin} disabled={saving} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditingEvent(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving} sx={{ minWidth: 140 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!confirmDelete}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={5000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Events;
